import { router } from '@inertiajs/react';
import { useState } from 'react';

import { RecordActions } from '@/components/admin/detail/RecordSheet';
import { Button } from '@/components/ui/Button';
import { trackUmamiEvent } from '@/lib/analytics';
import type { SubmissionStatus } from '@/types/api/submission-status';

interface StatusActionBarProps {
  /** Resource base path, e.g. "/messages" or "/requests". */
  basePath: string;
  id: number;
  status: SubmissionStatus;
  /** Gendered label, e.g. "Marcar como resolvida" / "…resolvido". */
  resolveLabel: string;
}

// Action bar for the pending/handled/dismissed flow (Messages + Pedidos).
// Renders as the record sheet's footer. Patches the resource's status endpoint;
// the controller redirects back to the detail page, so the new status shows in
// place.
export function StatusActionBar({ basePath, id, status, resolveLabel }: StatusActionBarProps) {
  const [loading, setLoading] = useState(false);

  function change(next: SubmissionStatus) {
    setLoading(true);
    trackUmamiEvent('admin_status_change', {
      record_type: basePath.replace(/^\//, ''),
      from_status: status,
      to_status: next,
    });
    router.patch(
      `${basePath}/${id}/status`,
      { status: next },
      { preserveScroll: true, onFinish: () => setLoading(false) },
    );
  }

  return (
    <RecordActions>
      {status !== 'handled' && (
        <Button variant="green" size="sm" disabled={loading} onClick={() => change('handled')}>
          <span className="material-symbols-outlined text-[18px]">check_circle</span>
          {resolveLabel}
        </Button>
      )}
      {status !== 'pending' && (
        <Button variant="white" size="sm" disabled={loading} onClick={() => change('pending')}>
          <span className="material-symbols-outlined text-[18px]">undo</span>
          Repor como pendente
        </Button>
      )}
      {status !== 'dismissed' && (
        <button
          type="button"
          disabled={loading}
          onClick={() => change('dismissed')}
          className="text-error hover:bg-error/10 ml-auto inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium opacity-80 transition-colors hover:opacity-100 disabled:opacity-50"
        >
          <span className="material-symbols-outlined text-[16px]">archive</span>
          Arquivar
        </button>
      )}
    </RecordActions>
  );
}
